import { useMemo, useState } from 'react';

const numberFormat = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 3 });
const moneyFormat = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function fmtQty(value) {
  if (value === null || value === undefined || value === '') return '—';
  return numberFormat.format(Number(value));
}

export function fmtMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  return moneyFormat.format(Number(value));
}

export function fmtTime(iso) {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
}

/** Triggers a browser download for bytes or text. */
export function downloadFile(name, data, type = 'application/octet-stream') {
  const blob = data instanceof Blob ? data : new Blob([data], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export function safeFileName(text) {
  return String(text || 'audit').replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80);
}

/**
 * Every screen starts with what it is for, what the user enters and what the
 * app works out, so operators can follow the sequence without a manual.
 */
export function StepHeader({ number, title, purpose, inputs, outputs, children }) {
  const [open, setOpen] = useState(false);
  return (
    <header className="rp-step-header">
      <div className="rp-step-header__top">
        <div>
          <p className="rp-step-header__number">Step {number} of 9</p>
          <h2>{title}</h2>
          <p className="rp-step-header__purpose">{purpose}</p>
        </div>
        <div className="rp-step-header__actions">
          {children}
          <button type="button" className="rp-link-button" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            {open ? 'Hide details' : 'What you enter and what is calculated'}
          </button>
        </div>
      </div>
      {open ? (
        <div className="rp-step-header__details">
          <div>
            <h3>You enter</h3>
            <ul>{inputs.map((item) => <li key={item}>{item}</li>)}</ul>
          </div>
          <div>
            <h3>The app calculates</h3>
            <ul>{outputs.map((item) => <li key={item}>{item}</li>)}</ul>
          </div>
        </div>
      ) : null}
    </header>
  );
}

/** Local date as YYYY-MM-DD for file names (toISOString would use UTC). */
export function todayStamp(date = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function Notice({ tone = 'info', title, children, action }) {
  return (
    <div className={`rp-notice rp-notice--${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      <span className="rp-notice__icon" aria-hidden="true">
        {tone === 'error' ? '!' : tone === 'warning' ? '!' : tone === 'success' ? '✓' : 'i'}
      </span>
      <div className="rp-notice__body">
        {title ? <strong className="rp-notice__title">{title}</strong> : null}
        {children ? <div>{children}</div> : null}
      </div>
      {action ? <div className="rp-notice__action">{action}</div> : null}
    </div>
  );
}

export function Pill({ tone = 'neutral', children }) {
  return <span className={`rp-pill rp-pill--${tone}`}>{children}</span>;
}

export function Stat({ label, value, note, tone }) {
  return (
    <div className={`rp-stat${tone ? ` rp-stat--${tone}` : ''}`}>
      <dt>{label}</dt>
      <dd>
        <span className="rp-stat__value">{value}</span>
        {note ? <span className="rp-stat__note">{note}</span> : null}
      </dd>
    </div>
  );
}

export function Tabs({ tabs, active, onChange }) {
  return (
    <div className="rp-tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={active === tab.id}
          className={active === tab.id ? 'is-active' : ''}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
          {tab.count !== undefined ? <span className="rp-tabs__count">{tab.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

/**
 * Compact, searchable, paginated table.
 * columns: [{ key, label, render?(row), align?, width?, search? (default true) }]
 */
export function DataTable({
  columns,
  rows,
  rowKey = (row) => row.id,
  pageSize = 50,
  searchPlaceholder = 'Search',
  emptyText = 'Nothing to show.',
  toolbar = null,
  rowClassName,
  initialSearch = '',
  searchable = true,
}) {
  const [query, setQuery] = useState(initialSearch);
  const [page, setPage] = useState(0);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    const keys = columns.filter((c) => c.search !== false).map((c) => c.searchValue || ((row) => row[c.key]));
    return rows.filter((row) =>
      keys.some((get) => {
        const value = get(row);
        return value !== null && value !== undefined && String(value).toLowerCase().includes(q);
      }),
    );
  }, [rows, query, columns]);

  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const current = Math.min(page, pages - 1);
  const visible = filtered.slice(current * pageSize, current * pageSize + pageSize);

  return (
    <div className="rp-table">
      {searchable || toolbar ? (
        <div className="rp-table__toolbar">
          {searchable ? (
            <input
              type="search"
              value={query}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(0);
              }}
            />
          ) : null}
          {toolbar}
        </div>
      ) : null}
      <div className="rp-table__scroll">
        <table>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key || column.label} className={column.align === 'right' ? 'is-num' : ''} style={column.width ? { minWidth: column.width } : undefined}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="rp-table__empty">
                  {query ? `No rows match "${query}".` : emptyText}
                </td>
              </tr>
            ) : (
              visible.map((row) => (
                <tr key={rowKey(row)} className={rowClassName ? rowClassName(row) : undefined}>
                  {columns.map((column) => (
                    <td key={column.key || column.label} className={column.align === 'right' ? 'is-num' : ''}>
                      {column.render ? column.render(row) : row[column.key] ?? ''}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="rp-table__footer">
        <span>
          {filtered.length === 0
            ? '0 rows'
            : `${(current * pageSize + 1).toLocaleString('en-IN')}–${Math.min((current + 1) * pageSize, filtered.length).toLocaleString('en-IN')} of ${filtered.length.toLocaleString('en-IN')}`}
          {filtered.length !== rows.length ? ` (filtered from ${rows.length.toLocaleString('en-IN')})` : ''}
        </span>
        {pages > 1 ? (
          <div className="rp-table__pager">
            <button type="button" disabled={current === 0} onClick={() => setPage(current - 1)}>
              Previous
            </button>
            <span>
              Page {current + 1} of {pages}
            </span>
            <button type="button" disabled={current >= pages - 1} onClick={() => setPage(current + 1)}>
              Next
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Small file picker that reads the chosen file as text or bytes. */
export function FilePick({ label, accept, onFile, disabled, directory = false, multiple = false }) {
  const extra = directory ? { webkitdirectory: '', directory: '', multiple: true } : multiple ? { multiple: true } : {};
  return (
    <label className={`rp-file${disabled ? ' is-disabled' : ''}`}>
      <input
        type="file"
        accept={accept}
        disabled={disabled}
        {...extra}
        onChange={(event) => {
          const files = [...(event.target.files || [])];
          event.target.value = '';
          if (files.length) onFile(directory || multiple ? files : files[0]);
        }}
      />
      <span>{label}</span>
    </label>
  );
}

export function Progress({ label, detail, value }) {
  return (
    <div className="rp-progress" role="status" aria-live="polite">
      <div className="rp-progress__text">
        <strong>{label}</strong>
        {detail ? <span>{detail}</span> : null}
      </div>
      <div className="rp-progress__track">
        <div className="rp-progress__fill" style={{ width: `${Math.max(3, Math.min(100, value ?? 100))}%` }} />
      </div>
    </div>
  );
}

export function History({ entries }) {
  if (!entries?.length) return null;
  return (
    <details className="rp-history">
      <summary>History ({entries.length})</summary>
      <ol>
        {entries.map((entry, index) => (
          <li key={index}>
            <time>{fmtTime(entry.t)}</time> {entry.action}
            {entry.note ? ` — ${entry.note}` : ''}
            {entry.by ? ` (${entry.by})` : ''}
          </li>
        ))}
      </ol>
    </details>
  );
}
